// FLOW-03a (Task 7.1; SPEC S2, S7) — como um gatilho de flow aparece nas telas da aba PowerFlow (a
// linha do flow, o detalhe do gatilho e o editor): o ícone do tipo, o resumo da regra ("a cada 5 min"),
// o "próxima: hoje 14:35", o desfecho de cada chamada ou batida e as contagens do dia. As telas só
// leem: nada aqui pede nada ao servidor.
//
// O gatilho chega na forma do `GET /api/v1/accounts/:id/powerflow/gatilhos` (Task 5.4): `{id,
// flow_name, gatilho_id, tipo, config, ligado, ligado_em, publicado, contagens_do_dia, url,
// url_de_teste | estado_da_agenda}`, com o `config` como o compilador o normalizou (`a_cada_s`). No
// editor, o `config` é o bloco do YAML (S1, `a_cada: 5m`): os dois valem aqui.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

export const ICONES_DOS_GATILHOS = {
  agenda: 'i-lucide-calendar-clock',
  webhook: 'i-lucide-webhook',
  agente: 'i-lucide-bot',
};

// Cada desfecho tem um glifo (a cor fica no glifo, o texto ao lado é neutro) e um grupo, que é o que
// as contagens do dia somam: o disparo (virou execução), a recusa (não virou, R6) e a pulada (Q6).
// São os que o Rails grava (`Webhooks::PowerflowController#registrar`) e os da batida da agenda
// (`duravel.DESFECHOS_DA_BATIDA`), e só eles (resposta 5 de UX, 28/10).
export const DESFECHOS = {
  // webhook
  aceita: { grupo: 'disparo', glifo: 'i-lucide-check text-n-teal-9' },
  respondida: { grupo: 'disparo', glifo: 'i-lucide-check-check text-n-teal-9' },
  erro: { grupo: 'disparo', glifo: 'i-lucide-x text-n-ruby-9' },
  timeout: { grupo: 'disparo', glifo: 'i-lucide-timer-off text-n-amber-11' },
  recusada_metodo: { grupo: 'recusa', glifo: 'i-lucide-ban text-n-ruby-9' },
  recusada_tamanho: { grupo: 'recusa', glifo: 'i-lucide-ban text-n-ruby-9' },
  recusada_segredo: {
    grupo: 'recusa',
    glifo: 'i-lucide-shield-x text-n-ruby-9',
  },
  recusada_teto: { grupo: 'recusa', glifo: 'i-lucide-gauge text-n-ruby-9' },
  recusada_params: { grupo: 'recusa', glifo: 'i-lucide-ban text-n-ruby-9' },
  fila_cheia: { grupo: 'recusa', glifo: 'i-lucide-gauge text-n-ruby-9' },
  // agenda
  disparou: { grupo: 'disparo', glifo: 'i-lucide-check text-n-teal-9' },
  pulou_sobreposicao: {
    grupo: 'pulada',
    glifo: 'i-lucide-skip-forward text-n-slate-10',
  },
  pulou_fila_cheia: {
    grupo: 'pulada',
    glifo: 'i-lucide-skip-forward text-n-amber-11',
  },
  erro_catalogo: { grupo: 'recusa', glifo: 'i-lucide-x text-n-ruby-9' },
};

// `a_cada: 5m` → `{ n: 5, unidade: 'm' }`. A unidade é `m`, `h` ou `d` (S3).
export function partesDoIntervalo(aCada) {
  const casou = /^(\d+)\s*([mhd])$/.exec(String(aCada || '').trim());
  return casou ? { n: Number(casou[1]), unidade: casou[2] } : null;
}

// O gatilho pausado que nunca foi ligado (`ligado_em` nulo): o que nasceu com a publicação (Q2).
export const nuncaLigado = gatilho => !gatilho.ligado && !gatilho.ligado_em;

// O intervalo do gatilho em `{n, unidade}`: o do YAML (`a_cada: 5m`, no editor) ou o normalizado
// pelo compilador (`a_cada_s: 300`, o `config` da lista). A unidade é `m`, `h` ou `d` (S3), a maior
// que divide.
export function intervaloDaRegra(config = {}) {
  if (config.a_cada_s) {
    const s = Number(config.a_cada_s);
    if (s % 86400 === 0) return { n: s / 86400, unidade: 'd' };
    if (s % 3600 === 0) return { n: s / 3600, unidade: 'h' };
    return { n: Math.round(s / 60), unidade: 'm' };
  }
  return partesDoIntervalo(config.a_cada);
}

// A regra do YAML do editor na forma do contrato (`{a_cada_s}` ou `{cron, fuso}`), para a prévia.
const SEGUNDOS = { m: 60, h: 3600, d: 86400 };
export function regraDoContrato(agenda = {}) {
  if (agenda.cron !== undefined) {
    return { cron: agenda.cron, fuso: agenda.fuso || 'America/Sao_Paulo' };
  }
  const partes = partesDoIntervalo(agenda.a_cada);
  return partes ? { a_cada_s: partes.n * SEGUNDOS[partes.unidade] } : null;
}

// As contagens de hoje (`{desfecho: quantas}`) somadas por grupo.
export function contagensPorGrupo(contagens = {}) {
  const total = { disparo: 0, recusa: 0, pulada: 0 };
  Object.entries(contagens || {}).forEach(([desfecho, n]) => {
    const grupo = DESFECHOS[desfecho]?.grupo;
    if (grupo) total[grupo] += n;
  });
  return total;
}

export function useGatilhoNaTela() {
  const { t, locale } = useI18n();
  const idioma = computed(() => String(locale.value || 'en').replace('_', '-'));

  const rotuloDoTipo = tipo =>
    ({
      agenda: t('POWER_TOOLS.POWERFLOW.TRIGGERS.KINDS.SCHEDULE'),
      webhook: t('POWER_TOOLS.POWERFLOW.TRIGGERS.KINDS.WEBHOOK'),
      agente: t('POWER_TOOLS.POWERFLOW.TRIGGERS.KINDS.AGENT'),
    })[tipo] || tipo;

  // O resumo da regra no selo: "a cada 5 min", a frase da prévia do cron (quando o PowerTools a
  // manda) ou o cron cru; no webhook, o método.
  function resumoDaRegra(gatilho) {
    const config = gatilho.config || {};
    if (gatilho.tipo === 'webhook') return config.metodo || 'POST';
    const intervalo = intervaloDaRegra(config);
    if (intervalo) {
      const { n, unidade } = intervalo;
      return {
        m: () => t('POWER_TOOLS.POWERFLOW.TRIGGERS.EVERY.m', { n }, n),
        h: () => t('POWER_TOOLS.POWERFLOW.TRIGGERS.EVERY.h', { n }, n),
        d: () => t('POWER_TOOLS.POWERFLOW.TRIGGERS.EVERY.d', { n }, n),
      }[unidade]();
    }
    return (
      gatilho.estado_da_agenda?.frase ||
      t('POWER_TOOLS.POWERFLOW.TRIGGERS.CRON', { expressao: config.cron })
    );
  }

  // "hoje 14:35", "amanhã 09:00" e, mais longe, "seg., 03/11, 09:00". No fuso da regra, que é o
  // que a frase da agenda diz ("horário de Brasília").
  // Com `segundos` (a tabela de chamadas, resposta 7 de UX), a hora leva os segundos.
  function quandoCurto(
    instante,
    fuso = 'America/Sao_Paulo',
    agora = new Date(),
    { segundos = false } = {}
  ) {
    const em = new Date(instante);
    if (Number.isNaN(em.getTime())) return '';
    const dia = d =>
      new Intl.DateTimeFormat('en-CA', { timeZone: fuso }).format(d);
    const relogio = {
      hour: '2-digit',
      minute: '2-digit',
      ...(segundos ? { second: '2-digit' } : {}),
    };
    const hora = new Intl.DateTimeFormat(idioma.value, {
      timeZone: fuso,
      ...relogio,
    }).format(em);
    const amanha = new Date(agora.getTime() + 24 * 60 * 60 * 1000);
    if (dia(em) === dia(agora)) {
      return t('POWER_TOOLS.POWERFLOW.TRIGGERS.TODAY', { hora });
    }
    if (dia(em) === dia(amanha)) {
      return t('POWER_TOOLS.POWERFLOW.TRIGGERS.TOMORROW', { hora });
    }
    return new Intl.DateTimeFormat(idioma.value, {
      timeZone: fuso,
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      ...relogio,
    }).format(em);
  }

  const rotulosDosDesfechos = computed(() => ({
    aceita: t('POWER_TOOLS.POWERFLOW.TRIGGERS.OUTCOMES.aceita'),
    respondida: t('POWER_TOOLS.POWERFLOW.TRIGGERS.OUTCOMES.respondida'),
    erro: t('POWER_TOOLS.POWERFLOW.TRIGGERS.OUTCOMES.erro'),
    timeout: t('POWER_TOOLS.POWERFLOW.TRIGGERS.OUTCOMES.timeout'),
    recusada_metodo: t(
      'POWER_TOOLS.POWERFLOW.TRIGGERS.OUTCOMES.recusada_metodo'
    ),
    recusada_tamanho: t(
      'POWER_TOOLS.POWERFLOW.TRIGGERS.OUTCOMES.recusada_tamanho'
    ),
    recusada_segredo: t(
      'POWER_TOOLS.POWERFLOW.TRIGGERS.OUTCOMES.recusada_segredo'
    ),
    recusada_teto: t('POWER_TOOLS.POWERFLOW.TRIGGERS.OUTCOMES.recusada_teto'),
    recusada_params: t(
      'POWER_TOOLS.POWERFLOW.TRIGGERS.OUTCOMES.recusada_params'
    ),
    fila_cheia: t('POWER_TOOLS.POWERFLOW.TRIGGERS.OUTCOMES.fila_cheia'),
    disparou: t('POWER_TOOLS.POWERFLOW.TRIGGERS.OUTCOMES.disparou'),
    pulou_sobreposicao: t(
      'POWER_TOOLS.POWERFLOW.TRIGGERS.OUTCOMES.pulou_sobreposicao'
    ),
    pulou_fila_cheia: t(
      'POWER_TOOLS.POWERFLOW.TRIGGERS.OUTCOMES.pulou_fila_cheia'
    ),
    erro_catalogo: t('POWER_TOOLS.POWERFLOW.TRIGGERS.OUTCOMES.erro_catalogo'),
  }));
  const rotuloDoDesfecho = desfecho =>
    rotulosDosDesfechos.value[desfecho] || desfecho;

  // "168 disparos", "39 recusas", "2 puladas" ("2 puladas hoje", na agenda), no número da língua da conta.
  const formatoDoNumero = computed(() => new Intl.NumberFormat(idioma.value));
  const contagem = (chave, n) => {
    const count = formatoDoNumero.value.format(n);
    return {
      FIRED: () =>
        t('POWER_TOOLS.POWERFLOW.TRIGGERS.COUNTS.FIRED', { count }, n),
      REFUSED: () =>
        t('POWER_TOOLS.POWERFLOW.TRIGGERS.COUNTS.REFUSED', { count }, n),
      SKIPPED: () =>
        t('POWER_TOOLS.POWERFLOW.TRIGGERS.COUNTS.SKIPPED', { count }, n),
      SKIPPED_TODAY: () =>
        t('POWER_TOOLS.POWERFLOW.TRIGGERS.COUNTS.SKIPPED_TODAY', { count }, n),
    }[chave]();
  };

  return {
    idioma,
    rotuloDoTipo,
    resumoDaRegra,
    quandoCurto,
    rotuloDoDesfecho,
    contagem,
  };
}
